import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  DispatchInput,
  DispatchQueryParams,
  dispatchService,
  GoodsReceiptInput,
  GoodsReceiptQueryParams,
  goodsReceiptService,
  ReceiveInput,
  StockTransferInput,
  StockTransferQueryParams,
  stockTransferService,
  WarehouseInput,
  WarehouseQueryParams,
  warehouseService,
} from '../services/warehouse.service';

export const useWarehousesPage = (params: WarehouseQueryParams) =>
  useQuery({ queryKey: ['warehouses', 'page', params], queryFn: () => warehouseService.getPage(params), placeholderData: keepPreviousData });

export const useWarehouseSummary = () => useQuery({ queryKey: ['warehouses', 'summary'], queryFn: () => warehouseService.getSummary() });

// The warehouses a form offers, default first
export const useActiveWarehouses = () =>
  useQuery({ queryKey: ['warehouses', 'active'], queryFn: () => warehouseService.getActive(), staleTime: 60_000 });

export const useWarehouseStock = (id: string | undefined, params: { page?: number; limit?: number; search?: string }) =>
  useQuery({
    queryKey: ['warehouses', 'stock', id, params],
    queryFn: () => warehouseService.getStock(id as string, params),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });

export const useProductLocations = (productId: string | undefined) =>
  useQuery({
    queryKey: ['warehouses', 'product-locations', productId],
    queryFn: () => warehouseService.getProductLocations(productId as string),
    enabled: !!productId,
  });

const useInvalidateWarehouses = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['warehouses'] });
};

export const useCreateWarehouse = () => {
  const invalidate = useInvalidateWarehouses();
  return useMutation({ mutationFn: (data: WarehouseInput) => warehouseService.create(data), onSuccess: invalidate });
};

export const useUpdateWarehouse = () => {
  const invalidate = useInvalidateWarehouses();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<WarehouseInput> }) => warehouseService.update(id, data),
    onSuccess: invalidate,
  });
};

export const useSetDefaultWarehouse = () => {
  const invalidate = useInvalidateWarehouses();
  return useMutation({ mutationFn: (id: string) => warehouseService.setDefault(id), onSuccess: invalidate });
};

export const useDeleteWarehouse = () => {
  const invalidate = useInvalidateWarehouses();
  return useMutation({ mutationFn: (id: string) => warehouseService.remove(id), onSuccess: invalidate });
};

export const useStockTransfersPage = (params: StockTransferQueryParams) =>
  useQuery({ queryKey: ['stockTransfers', 'page', params], queryFn: () => stockTransferService.getPage(params), placeholderData: keepPreviousData });

export const useStockTransferSummary = () =>
  useQuery({ queryKey: ['stockTransfers', 'summary'], queryFn: () => stockTransferService.getSummary() });

export const useStockTransfer = (id: string | undefined) =>
  useQuery({ queryKey: ['stockTransfers', 'one', id], queryFn: () => stockTransferService.getOne(id as string), enabled: !!id });

// A transfer moves stock, so the warehouses and the product lists are refreshed with it
const useInvalidateTransfers = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['stockTransfers'] });
    queryClient.invalidateQueries({ queryKey: ['warehouses'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
  };
};

export const useCreateStockTransfer = () => {
  const invalidate = useInvalidateTransfers();
  return useMutation({ mutationFn: (data: StockTransferInput) => stockTransferService.create(data), onSuccess: invalidate });
};

export const useUpdateStockTransfer = () => {
  const invalidate = useInvalidateTransfers();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<StockTransferInput> }) => stockTransferService.update(id, data),
    onSuccess: invalidate,
  });
};

export const useShipStockTransfer = () => {
  const invalidate = useInvalidateTransfers();
  return useMutation({ mutationFn: (id: string) => stockTransferService.ship(id), onSuccess: invalidate });
};

export const useReceiveStockTransfer = () => {
  const invalidate = useInvalidateTransfers();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReceiveInput }) => stockTransferService.receive(id, data),
    onSuccess: invalidate,
  });
};

export const useCancelStockTransfer = () => {
  const invalidate = useInvalidateTransfers();
  return useMutation({ mutationFn: (id: string) => stockTransferService.cancel(id), onSuccess: invalidate });
};

export const useDeleteStockTransfer = () => {
  const invalidate = useInvalidateTransfers();
  return useMutation({ mutationFn: (id: string) => stockTransferService.remove(id), onSuccess: invalidate });
};

// ---- Goods receipts (GRN)

export const useGoodsReceiptsPage = (params: GoodsReceiptQueryParams) =>
  useQuery({ queryKey: ['goodsReceipts', 'page', params], queryFn: () => goodsReceiptService.getPage(params), placeholderData: keepPreviousData });

export const useGoodsReceiptSummary = () => useQuery({ queryKey: ['goodsReceipts', 'summary'], queryFn: () => goodsReceiptService.getSummary() });

export const useGoodsReceipt = (id: string | undefined) =>
  useQuery({ queryKey: ['goodsReceipts', 'one', id], queryFn: () => goodsReceiptService.getOne(id as string), enabled: !!id });

export const useReceivableOrders = (enabled: boolean) =>
  useQuery({ queryKey: ['goodsReceipts', 'receivable'], queryFn: () => goodsReceiptService.getReceivableOrders(), enabled });

export const usePendingOrder = (purchaseOrderId: string | undefined) =>
  useQuery({
    queryKey: ['goodsReceipts', 'pending', purchaseOrderId],
    queryFn: () => goodsReceiptService.getPending(purchaseOrderId as string),
    enabled: !!purchaseOrderId,
  });

// A receipt moves stock and changes its purchase order
const useInvalidateReceipts = () => {
  const queryClient = useQueryClient();
  return () => {
    ['goodsReceipts', 'warehouses', 'products', 'purchaseOrders', 'purchase-orders', 'purchase'].forEach((key) =>
      queryClient.invalidateQueries({ queryKey: [key] }),
    );
  };
};

export const useCreateGoodsReceipt = () => {
  const invalidate = useInvalidateReceipts();
  return useMutation({ mutationFn: (data: GoodsReceiptInput) => goodsReceiptService.create(data), onSuccess: invalidate });
};

export const useCancelGoodsReceipt = () => {
  const invalidate = useInvalidateReceipts();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => goodsReceiptService.cancel(id, reason),
    onSuccess: invalidate,
  });
};

// ---- Delivery challans (dispatch)

export const useDispatchesPage = (params: DispatchQueryParams) =>
  useQuery({ queryKey: ['dispatches', 'page', params], queryFn: () => dispatchService.getPage(params), placeholderData: keepPreviousData });

export const useDispatchSummary = () => useQuery({ queryKey: ['dispatches', 'summary'], queryFn: () => dispatchService.getSummary() });

export const useDispatch = (id: string | undefined) =>
  useQuery({ queryKey: ['dispatches', 'one', id], queryFn: () => dispatchService.getOne(id as string), enabled: !!id });

export const useDispatchableOrders = () => useQuery({ queryKey: ['dispatches', 'dispatchable'], queryFn: () => dispatchService.getDispatchableOrders() });

export const usePendingSalesOrder = (salesOrderId: string | undefined) =>
  useQuery({
    queryKey: ['dispatches', 'pending', salesOrderId],
    queryFn: () => dispatchService.getPending(salesOrderId as string),
    enabled: !!salesOrderId,
  });

// A challan moves stock and changes its sales order
const useInvalidateDispatches = () => {
  const queryClient = useQueryClient();
  return () => {
    ['dispatches', 'warehouses', 'products', 'salesOrders', 'sales-orders', 'sales', 'quotes'].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
  };
};

export const useCreateDispatch = () => {
  const invalidate = useInvalidateDispatches();
  return useMutation({ mutationFn: (data: DispatchInput) => dispatchService.create(data), onSuccess: invalidate });
};

export const useRaiseInvoiceFromChallan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dispatchService.raiseInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatches'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
};

export const useCancelDispatch = () => {
  const invalidate = useInvalidateDispatches();
  return useMutation({ mutationFn: ({ id, reason }: { id: string; reason: string }) => dispatchService.cancel(id, reason), onSuccess: invalidate });
};
